import { useState, useEffect } from 'react'
import AdminLayout from '../../components/admin/AdminLayout'
import { useEntityCRUD } from '../../hooks/useEntityCRUD'
import { useAdminLog } from '../../hooks/useAdminLog'
import {Calendar, Clock, MapPin, Video, Church, Users, Plus, Edit2, Trash2, Search, X, ExternalLink, Repeat, Pin} from 'lucide-react'

type RecurrenceType = 'none' | 'weekly'

interface ProgramEvent {
  _id: string
  title: string
  description: string
  type: 'LIVE' | 'CULTO' | 'ATIVIDADE'
  date: string
  time: string
  location?: string
  liveUrl?: string
  imageUrl?: string
  status: 'ACTIVE' | 'COMPLETED'
  creator: string
  createdAt: string
  updatedAt: string
  // Recurrence fields
  recurrence: RecurrenceType
  recurrenceDays?: number[]   // 0=Dom, 1=Seg, ..., 6=Sáb
}

const WEEK_DAYS = [
  { label: 'Dom', value: 0 },
  { label: 'Seg', value: 1 },
  { label: 'Ter', value: 2 },
  { label: 'Qua', value: 3 },
  { label: 'Qui', value: 4 },
  { label: 'Sex', value: 5 },
  { label: 'Sáb', value: 6 },
]

const WEEK_DAYS_FULL = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado']

export function formatRecurrenceDays(days: number[]): string {
  if (!days || days.length === 0) return ''
  const sorted = [...days].sort((a, b) => a - b)
  if (sorted.length === 7) return 'Todos os dias'
  if (sorted.length === 1) return `Toda ${WEEK_DAYS_FULL[sorted[0]]}`
  return sorted.map(d => WEEK_DAYS[d].label).join(', ') + ' (semanal)'
}

export default function ProgramacaoManagement() {
  const [searchTerm, setSearchTerm] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  const [editingComic, setEditingComic] = useState<ProgramEvent | null>(null)
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'CULTO' as 'LIVE' | 'CULTO' | 'ATIVIDADE',
    date: '',
    time: '',
    location: '',
    liveUrl: '',
    imageUrl: '',
    status: 'ACTIVE' as 'ACTIVE' | 'COMPLETED',
    recurrence: 'none' as RecurrenceType,
    recurrenceDays: [] as number[],
  })

  const { data: events, loading, fetchData, createRecord, updateRecord, deleteRecord } = useEntityCRUD<ProgramEvent>('kids_comics')
  const { logAction } = useAdminLog()

  useEffect(() => {
    fetchData()
  }, [])

  const filteredEvents = events.filter(event =>
    event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (event.location && event.location.toLowerCase().includes(searchTerm.toLowerCase()))
  )

  // Separate recurring from one-time events
  const recurringEvents = filteredEvents.filter(e => e.recurrence === 'weekly')
  const oneTimeEvents = filteredEvents.filter(e => e.recurrence !== 'weekly')

  const toggleRecurrenceDay = (day: number) => {
    const current = formData.recurrenceDays
    if (current.includes(day)) {
      setFormData({ ...formData, recurrenceDays: current.filter(d => d !== day) })
    } else {
      setFormData({ ...formData, recurrenceDays: [...current, day] })
    }
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (formData.recurrence === 'weekly' && formData.recurrenceDays.length === 0) {
      alert('Selecione pelo menos um dia da semana para o evento recorrente.')
      return
    }
    try {
      await createRecord({
        ...formData,
        date: formData.recurrence === 'weekly' ? '' : formData.date,
        creator: 'admin',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })
      await logAction('CREATE', 'kids_comics', '', `Evento criado: ${formData.title}`)
      resetForm()
      fetchData()
    } catch (error) {
      console.error('Erro ao criar evento:', error)
      alert('Erro ao criar evento')
    }
  }

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingComic) return
    if (formData.recurrence === 'weekly' && formData.recurrenceDays.length === 0) {
      alert('Selecione pelo menos um dia da semana para o evento recorrente.')
      return
    }
    try {
      await updateRecord(editingComic._id, {
        ...formData,
        date: formData.recurrence === 'weekly' ? '' : formData.date,
        updatedAt: new Date().toISOString()
      })
      await logAction('UPDATE', 'kids_comics', editingComic._id, `Evento atualizado: ${formData.title}`)
      resetForm()
      fetchData()
    } catch (error) {
      console.error('Erro ao atualizar evento:', error)
      alert('Erro ao atualizar evento')
    }
  }

  const handleDelete = async (event: ProgramEvent) => {
    if (!confirm(`Confirma exclusão do evento "${event.title}"?`)) return
    try {
      await deleteRecord(event._id)
      await logAction('DELETE', 'kids_comics', event._id, `Evento excluído: ${event.title}`)
      fetchData()
    } catch (error) {
      console.error('Erro ao excluir evento:', error)
      alert('Erro ao excluir evento')
    }
  }

  const handleEdit = (event: ProgramEvent) => {
    setEditingComic(event)
    setFormData({
      title: event.title,
      description: event.description,
      type: event.type,
      date: event.date || '',
      time: event.time,
      location: event.location || '',
      liveUrl: event.liveUrl || '',
      imageUrl: event.imageUrl || '',
      status: event.status,
      recurrence: event.recurrence || 'none',
      recurrenceDays: event.recurrenceDays || [],
    })
    setIsCreating(true)
  }

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      type: 'CULTO',
      date: '',
      time: '',
      location: '',
      liveUrl: '',
      imageUrl: '',
      status: 'ACTIVE',
      recurrence: 'none',
      recurrenceDays: [],
    })
    setEditingComic(null)
    setIsCreating(false)
  }

  const renderEventCard = (event: ProgramEvent) => (
    <div key={event._id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
      <div className="flex flex-col md:flex-row">
        <div className="md:w-1/4 relative">
          {event.imageUrl ? (
            <img
              src={event.imageUrl}
              alt={event.title}
              className="w-full h-48 md:h-full object-cover"
            />
          ) : (
            <div className="w-full h-48 md:h-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
              <Calendar className="text-white" size={48} />
            </div>
          )}
          <div className="absolute top-2 left-2 flex flex-col gap-1">
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
              event.type === 'LIVE' ? 'bg-red-500 text-white' :
              event.type === 'CULTO' ? 'bg-blue-500 text-white' :
              'bg-green-500 text-white'
            }`}>
              {event.type}
            </span>
            {event.recurrence === 'weekly' && (
              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-amber-500 text-white flex items-center gap-1">
                <Repeat size={10} />
                Fixo
              </span>
            )}
          </div>
          <div className="absolute top-2 right-2">
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
              event.status === 'ACTIVE' ? 'bg-green-500 text-white' : 'bg-gray-500 text-white'
            }`}>
              {event.status === 'ACTIVE' ? 'Ativo' : 'Concluído'}
            </span>
          </div>
        </div>
        <div className="md:w-3/4 p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-2">{event.title}</h3>
          <p className="text-gray-600 mb-3 line-clamp-2">{event.description}</p>
          <div className="flex flex-wrap gap-4 mb-4 text-sm text-gray-700">
            <div className="flex items-center gap-1">
              <Calendar size={16} className="text-blue-600" />
              {event.recurrence === 'weekly'
                ? <span className="text-amber-700 font-semibold">{formatRecurrenceDays(event.recurrenceDays || [])}</span>
                : (event.date ? new Date(event.date).toLocaleDateString('pt-BR') : '—')
              }
            </div>
            <div className="flex items-center gap-1">
              <Clock size={16} className="text-blue-600" />
              {event.time}
            </div>
            {event.location && (
              <div className="flex items-center gap-1">
                <MapPin size={16} className="text-blue-600" />
                {event.location}
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {event.liveUrl && (
              <a
                href={event.liveUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-purple-50 text-purple-600 px-4 py-2 rounded-lg hover:bg-purple-100 transition-colors text-sm font-medium"
              >
                <ExternalLink size={16} />
                Ver Link
              </a>
            )}
            <button
              onClick={() => handleEdit(event)}
              className="flex items-center gap-2 bg-blue-50 text-blue-600 px-4 py-2 rounded-lg hover:bg-blue-100 transition-colors text-sm"
            >
              <Edit2 size={16} />
              Editar
            </button>
            <button
              onClick={() => handleDelete(event)}
              className="flex items-center gap-2 bg-red-50 text-red-600 px-4 py-2 rounded-lg hover:bg-red-100 transition-colors text-sm"
            >
              <Trash2 size={16} />
              Excluir
            </button>
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Gestão de Programação</h1>
            <p className="text-gray-600 mt-1">Gerenciar lives, cultos e atividades do ministério — REFUGE IN CHRIST MINISTRY</p>
          </div>
          <button
            onClick={() => setIsCreating(true)}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus size={20} />
            Novo Evento
          </button>
        </div>

        {/* Create/Edit Form */}
        {isCreating && (
          <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-blue-600">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">
                {editingComic ? 'Editar Evento' : 'Adicionar Novo Evento'}
              </h2>
              <button onClick={resetForm} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={editingComic ? handleUpdate : handleCreate} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Título *</label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo *</label>
                  <select
                    required
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="LIVE">Live</option>
                    <option value="CULTO">Culto</option>
                    <option value="ATIVIDADE">Atividade</option>
                  </select>
                </div>

                {/* Recurrence Toggle */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Agendamento *</label>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, recurrence: 'none', recurrenceDays: [] })}
                      className={`flex items-center gap-2 px-5 py-2.5 rounded-lg border-2 font-semibold transition-all ${
                        formData.recurrence === 'none'
                          ? 'border-blue-600 bg-blue-50 text-blue-700'
                          : 'border-gray-200 text-gray-500 hover:border-gray-300'
                      }`}
                    >
                      <Calendar size={18} />
                      Data específica
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, recurrence: 'weekly', date: '' })}
                      className={`flex items-center gap-2 px-5 py-2.5 rounded-lg border-2 font-semibold transition-all ${
                        formData.recurrence === 'weekly'
                          ? 'border-amber-500 bg-amber-50 text-amber-700'
                          : 'border-gray-200 text-gray-500 hover:border-gray-300'
                      }`}
                    >
                      <Repeat size={18} />
                      Evento fixo (recorrente)
                    </button>
                  </div>
                </div>

                {/* Data Específica */}
                {formData.recurrence === 'none' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Data *</label>
                    <input
                      type="date"
                      required
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}

                {/* Dias da Semana (Recorrente) */}
                {formData.recurrence === 'weekly' && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Dias da semana * <span className="text-amber-600 font-normal">(ocorre toda semana nos dias selecionados)</span>
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {WEEK_DAYS.map(day => (
                        <button
                          key={day.value}
                          type="button"
                          onClick={() => toggleRecurrenceDay(day.value)}
                          className={`w-14 h-14 rounded-xl font-bold text-sm transition-all border-2 ${
                            formData.recurrenceDays.includes(day.value)
                              ? 'bg-amber-500 border-amber-500 text-white shadow-md scale-105'
                              : 'bg-white border-gray-200 text-gray-600 hover:border-amber-400 hover:text-amber-600'
                          }`}
                        >
                          {day.label}
                        </button>
                      ))}
                    </div>
                    {formData.recurrenceDays.length > 0 && (
                      <p className="text-xs text-amber-700 mt-2 font-medium">
                        ✓ {formatRecurrenceDays(formData.recurrenceDays)}
                      </p>
                    )}
                  </div>
                )}

                {/* Horário */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Horário *</label>
                  <input
                    type="time"
                    required
                    value={formData.time}
                    onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Local</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="Ex: Templo Central"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status *</label>
                  <select
                    required
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="ACTIVE">Ativo</option>
                    <option value="COMPLETED">Concluído</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Link (YouTube/Facebook/etc)</label>
                  <input
                    type="url"
                    value={formData.liveUrl}
                    onChange={(e) => setFormData({ ...formData, liveUrl: e.target.value })}
                    placeholder="https://youtube.com/live/..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">URL da Imagem</label>
                  <input
                    type="url"
                    value={formData.imageUrl}
                    onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                    placeholder="https://..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrição *</label>
                <textarea
                  rows={3}
                  required
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Breve descrição do evento..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {editingComic ? 'Atualizar Evento' : 'Adicionar Evento'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-6 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Search */}
        <div className="bg-white p-4 rounded-lg shadow-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Buscar eventos por título ou local..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Events List */}
        {loading ? (
          <div className="bg-white p-8 rounded-lg shadow-md text-center text-gray-500">
            Carregando eventos...
          </div>
        ) : filteredEvents.length === 0 ? (
          <div className="bg-white p-8 rounded-lg shadow-md text-center text-gray-500">
            Nenhum evento encontrado
          </div>
        ) : (
          <div className="space-y-6">
            {/* Eventos Fixos / Recorrentes */}
            {recurringEvents.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Pin size={18} className="text-amber-600" />
                  <h2 className="text-lg font-bold text-amber-700">Eventos Fixos (Recorrentes)</h2>
                  <span className="bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded-full font-semibold">
                    {recurringEvents.length}
                  </span>
                </div>
                <div className="space-y-4 border-l-4 border-amber-400 pl-4">
                  {recurringEvents.map(renderEventCard)}
                </div>
              </div>
            )}

            {/* Eventos Pontuais */}
            {oneTimeEvents.length > 0 && (
              <div>
                {recurringEvents.length > 0 && (
                  <div className="flex items-center gap-2 mb-3 mt-6">
                    <Calendar size={18} className="text-blue-600" />
                    <h2 className="text-lg font-bold text-blue-700">Eventos com Data Específica</h2>
                    <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full font-semibold">
                      {oneTimeEvents.length}
                    </span>
                  </div>
                )}
                <div className="space-y-4">
                  {oneTimeEvents.map(renderEventCard)}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total de Eventos</p>
                <p className="text-2xl font-bold text-gray-900">{events.length}</p>
              </div>
              <Calendar className="text-blue-600" size={32} />
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Lives</p>
                <p className="text-2xl font-bold text-gray-900">
                  {events.filter(e => e.type === 'LIVE').length}
                </p>
              </div>
              <Video className="text-red-600" size={32} />
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Cultos</p>
                <p className="text-2xl font-bold text-gray-900">
                  {events.filter(e => e.type === 'CULTO').length}
                </p>
              </div>
              <Church className="text-blue-600" size={32} />
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Fixos / Recorrentes</p>
                <p className="text-2xl font-bold text-amber-600">
                  {events.filter(e => e.recurrence === 'weekly').length}
                </p>
              </div>
              <Repeat className="text-amber-500" size={32} />
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  )
}
